import { useEffect, useRef } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useApiWithAuth } from "./useAuth.js";
import {
  clearOnboardingChoices,
  getOnboardingPawMatchPrefs,
  getOnboardingUserType,
  type OnboardingPawMatchPrefs,
} from "../Components/onboarding/onboardingState.js";

function buildPawMatchBody(prefs: OnboardingPawMatchPrefs) {
  const speciesMode = prefs.speciesMode;
  const wantsDogs = speciesMode === "dogs" || speciesMode === "both";
  const wantsCats = speciesMode === "cats" || speciesMode === "both";

  return {
    speciesMode: speciesMode ?? null,
    dogs: wantsDogs
      ? { size: prefs.size ?? null, energyLevel: prefs.energyLevel ?? null }
      : null,
    cats: wantsCats ? { size: prefs.size ?? null } : null,
  };
}

/**
 * After the first authenticated session, pushes the choices captured during the
 * (pre-login) onboarding carousel to the backend, then clears them locally.
 *
 * - userType (`adopter | visitor`) -> PUT /api/user/settings
 * - PawMatch preferences -> PUT /api/pawmatch/preferences
 *
 * Publisher intent (`wantsToPublish`) is intentionally NOT synced — it routes to
 * the Join Chooser during onboarding and is never persisted as a userType.
 */
export function useOnboardingSync(): void {
  const { isAuthenticated } = useAuth0();
  const { apiCall } = useApiWithAuth();
  const syncedRef = useRef(false);

  useEffect(() => {
    if (!isAuthenticated || syncedRef.current) return;
    syncedRef.current = true;

    let cancelled = false;

    const sync = async () => {
      const [userType, prefs] = await Promise.all([
        getOnboardingUserType(),
        getOnboardingPawMatchPrefs(),
      ]);

      if (cancelled) return;
      if (!userType && !prefs) return;

      try {
        if (userType) {
          await apiCall("/api/user/settings", {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ userType }),
          });
        }

        if (prefs && prefs.speciesMode) {
          await apiCall("/api/pawmatch/preferences", {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(buildPawMatchBody(prefs)),
          });
        }

        await clearOnboardingChoices();
      } catch (error) {
        // Allow a retry on a later session if the network failed.
        syncedRef.current = false;
        console.error("Failed to sync onboarding choices:", error);
      }
    };

    sync();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, apiCall]);
}
